import { logger } from '../logger.js';

import { isTeamsAppSuspended } from './teams-suspension.js';

/**
 * Why the app just lost the user's attention: the page was hidden, the page is being torn down, or
 * Teams is about to suspend the tab it keeps cached.
 */
export type AppBackgroundReason = 'hidden' | 'pagehide' | 'teams-suspend';

/** Called when the app is in front of the user again. */
export type AppForegroundListener = () => void;

/** Called when the app goes to the background; a returned promise is awaited by whoever asked. */
export type AppBackgroundListener = (
  reason: AppBackgroundReason,
) => Promise<void> | void;

const foregroundListeners = new Set<AppForegroundListener>();
const backgroundListeners = new Set<AppBackgroundListener>();
let documentListenersInstalled = false;

/**
 * One place that says when the app comes back to the user. A plain `visibilitychange` is not enough
 * inside Teams: a suspended tab is hidden by its host while its own document still reports
 * `visible`, so the Teams resume handler calls {@link notifyAppForeground} too.
 *
 * @returns a function that removes the listener.
 */
export function onAppForeground(listener: AppForegroundListener): () => void {
  installDocumentListeners();
  foregroundListeners.add(listener);
  return () => {
    foregroundListeners.delete(listener);
  };
}

/**
 * One place that says when the app leaves the user: the page is hidden, the page is going away, or
 * Teams is suspending the tab. Unsaved work is flushed from here.
 *
 * @returns a function that removes the listener.
 */
export function onAppBackground(listener: AppBackgroundListener): () => void {
  installDocumentListeners();
  backgroundListeners.add(listener);
  return () => {
    backgroundListeners.delete(listener);
  };
}

/**
 * Whether the app is in front of the user right now: its page is visible and Teams is not keeping the
 * tab suspended. A suspended tab still reports `visible`, so visibility alone does not mean presence.
 */
export function isAppInFront(): boolean {
  return document.visibilityState === 'visible' && !isTeamsAppSuspended();
}

/** Tells every foreground listener the app is back; one failing listener never stops the rest. */
export function notifyAppForeground(): void {
  for (const listener of [...foregroundListeners]) {
    runForegroundListener(listener);
  }
}

/**
 * Tells every background listener the app is leaving and waits for all of them to settle. It never
 * rejects, so a caller with a deadline (the Teams suspend handler) can always finish on time.
 */
export async function notifyAppBackground(
  reason: AppBackgroundReason,
): Promise<void> {
  await Promise.allSettled(
    [...backgroundListeners].map((listener) =>
      runBackgroundListener(listener, reason),
    ),
  );
}

function runForegroundListener(listener: AppForegroundListener): void {
  try {
    listener();
  } catch (error: unknown) {
    logger.debug('app-presence: foreground listener failed', error);
  }
}

async function runBackgroundListener(
  listener: AppBackgroundListener,
  reason: AppBackgroundReason,
): Promise<void> {
  try {
    await listener(reason);
  } catch (error: unknown) {
    logger.debug('app-presence: background listener failed', error);
  }
}

function installDocumentListeners(): void {
  if (documentListenersInstalled || typeof document === 'undefined') return;
  documentListenersInstalled = true;
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', handlePageHide);
}

function handleVisibilityChange(): void {
  if (document.visibilityState === 'visible') {
    notifyAppForeground();
    return;
  }
  void notifyAppBackground('hidden');
}

function handlePageHide(): void {
  void notifyAppBackground('pagehide');
}

/** Test-only: forgets every listener and the document hooks. */
export function _resetAppPresenceForTests(): void {
  foregroundListeners.clear();
  backgroundListeners.clear();
  if (documentListenersInstalled && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pagehide', handlePageHide);
  }
  documentListenersInstalled = false;
}
